/**
 * Editor utility functions for text conversion
 */

import { Editor } from "@tiptap/react"

/**
 * Definition line patterns where formatting should only be allowed AFTER the colon.
 * These are lines where the part before the colon is a label/identifier (not user-facing).
 */
const DEFINITION_LINE_PATTERNS = [
  /^Q\s+[^:]+:/i,           // Q label: text
  /^QUESTION\s+[^:]+:/i,    // QUESTION label: text
  /^TEXT\s+[^:]+:/i,        // TEXT label: text
  /^NOTE\s+[^:]+:/i,        // NOTE label: text
]

/**
 * Lines where NO formatting should be allowed (entire line is a label/keyword).
 */
const NO_FORMAT_LINE_PATTERNS = [
  /^LIST\s+\S+$/i,          // LIST name (no colon, entire line is label)
  /^SECTION\s*$/i,          // SECTION keyword
  /^EXCEPTIONS\s*$/i,       // EXCEPTIONS keyword
  /^#/,                     // Directive lines (#DEFINE, #LANGUAGE, etc.)
  /^SELECT\s+(ONE|MULTIPLE|RANGE)\s+FROM/i,  // SELECT keywords
  /^FILL\s*IN/i,            // FILL IN keywords
  /^PER\s+/i,               // PER keyword with list reference
]

/**
 * Check if the current cursor position allows bold/italic formatting.
 * Returns true if formatting is allowed, false if it should be blocked.
 *
 * Rules:
 * - Definition lines (Q, TEXT, NOTE): Only allow formatting AFTER the colon
 * - Label-only lines (LIST, SECTION, keywords): No formatting allowed
 * - List items and other content: Formatting allowed
 */
export const canApplyFormatting = (editor: Editor | null): boolean => {
  if (!editor) return false

  const { from, to } = editor.state.selection
  const doc = editor.state.doc

  // Check all nodes in selection range
  let canFormat = true

  doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === "paragraph") {
      const lineText = node.textContent
      const lineStart = pos + 1 // +1 to account for paragraph node start

      // Check if this is a no-format line
      for (const pattern of NO_FORMAT_LINE_PATTERNS) {
        if (pattern.test(lineText.trim())) {
          canFormat = false
          return false // Stop iteration
        }
      }

      // Check if this is a definition line (has label before colon)
      for (const pattern of DEFINITION_LINE_PATTERNS) {
        if (pattern.test(lineText.trim())) {
          // Find the colon position in this line
          const colonIndex = lineText.indexOf(":")
          if (colonIndex !== -1) {
            // Selection must be entirely after the colon
            const selectionStartInLine = from - lineStart
            const selectionEndInLine = to - lineStart

            if (selectionStartInLine <= colonIndex || selectionEndInLine <= colonIndex) {
              canFormat = false
              return false // Stop iteration
            }
          }
          break
        }
      }
    }
  })

  return canFormat
}

/**
 * Get a tooltip message explaining why formatting is disabled.
 */
export const getFormattingDisabledReason = (editor: Editor | null): string | null => {
  if (!editor) return null
  if (canApplyFormatting(editor)) return null

  return "Formatting not allowed in labels or keywords"
}

/**
 * Strip comment span tags from HTML while preserving inner content.
 * Comment marks in TipTap wrap text in <span data-comment-id="..."> tags.
 * These must be removed before saving to prevent backend parser errors.
 */
export const stripCommentSpans = (html: string): string => {
  // Remove <span data-comment-id="..." class="comment-highlight">...</span> but keep inner content
  // Handle both self-closing style and nested content
  return html.replace(/<span[^>]*data-comment-id="[^"]*"[^>]*>([\s\S]*?)<\/span>/gi, "$1")
}

/**
 * Filters out variant definition lines from content
 */
export const filterVariantDefinitions = (content: string): string => {
  if (!content) return content
  const lines = content.split("\n")
  const filteredLines = lines.filter((line) => {
    return (
      !line.startsWith("#DEFINE VARIANT") &&
      !line.startsWith("#LANGUAGE") &&
      !line.startsWith("#LINE 0")
    )
  })
  return filteredLines.join("\n")
}

/**
 * Converts HTML content back to plain text for saving,
 * while preserving <b> and <i> formatting tags.
 */
export const htmlToPlainText = (html: string): string => {
  // Step 1: Preserve <b>, </b>, <i>, </i> by replacing with placeholders
  // Also handle <strong>/<em> which TipTap may use
  const BOLD_OPEN = "\u0001BOLD_OPEN\u0001"
  const BOLD_CLOSE = "\u0001BOLD_CLOSE\u0001"
  const ITALIC_OPEN = "\u0001ITALIC_OPEN\u0001"
  const ITALIC_CLOSE = "\u0001ITALIC_CLOSE\u0001"

  let text = html
    .replace(/<b>/gi, BOLD_OPEN)
    .replace(/<\/b>/gi, BOLD_CLOSE)
    .replace(/<strong>/gi, BOLD_OPEN)
    .replace(/<\/strong>/gi, BOLD_CLOSE)
    .replace(/<i>/gi, ITALIC_OPEN)
    .replace(/<\/i>/gi, ITALIC_CLOSE)
    .replace(/<em>/gi, ITALIC_OPEN)
    .replace(/<\/em>/gi, ITALIC_CLOSE)

  // Step 2: Strip other HTML tags and process paragraph formatting
  text = text
    .replace(/<p>&nbsp;<\/p>/g, "\n")
    .replace(/<p>/g, "")
    .replace(/<\/p>/g, "\n")
    .replace(/<[^>]*>/g, "")  // Remove remaining HTML tags
    .replace(/&nbsp;/g, "")
    .replace(/&lt;&lt;/g, "<<")
    .replace(/&gt;&gt;/g, ">>")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .trim()

  // Step 3: Restore <b> and <i> tags
  text = text
    .replace(new RegExp(BOLD_OPEN, 'g'), "<b>")
    .replace(new RegExp(BOLD_CLOSE, 'g'), "</b>")
    .replace(new RegExp(ITALIC_OPEN, 'g'), "<i>")
    .replace(new RegExp(ITALIC_CLOSE, 'g'), "</i>")

  return filterVariantDefinitions(text)
}
